import { Box, CircularProgress } from '@material-ui/core';
import { formatDateTimeReadable, patchRemoveTagFromPricing } from '@nemuru-eng/js-api-client';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { PricingSpecsTable } from '@/components/modules/AgentDetail/ProvidersDetails/components/AgentPricings/components/PricingSpecsTable/PricingSpecsTable';
import { PricingNameForm } from '@/components/modules/PricingEditor/components/PricingNameForm';
import { PricingSpecByIdForm } from '@/components/modules/PricingEditor/components/PricingSpecByIdForm';
import { PricingSpecForm } from '@/components/modules/PricingEditor/components/PricingSpecForm';
import { PricingTagForm } from '@/components/modules/PricingEditor/components/PricingTagForm';
import { RemoveAllPricingInLenderContextForm } from '@/components/modules/PricingEditor/components/RemoveAllPricingInLenderContextForm';
import { usePricingEditor } from '@/components/modules/PricingEditor/hooks/usePricingEditor';
import { Action } from '@/components/modules/PricingEditor/types/Action';
import { Font } from '@/components/shared/components/Font';
import { ActionsOption } from '@/components/shared/components/MoreActionsMenu/types/ActionsOption';
import { PendingDataWarning } from '@/components/shared/components/PendingDataWarning';
import { PricingTag } from '@/components/shared/components/PricingsTable/components/PricingTag';
import { SubSectionWrapper } from '@/components/shared/components/SubSectionWrapper';
import { Text } from '@/components/shared/components/Text';
import { useRequest } from '@/hooks/requests/useRequest';
import { withPermissionsModule } from '@/utils/withPermissionsModule';

const PricingEditor = () => {
  const { pricingId } = useParams();
  const { pricing, getPricing, resetPricing, loading } = usePricingEditor(pricingId as string);
  const { request } = useRequest();
  const [action, setAction] = useState<Action | undefined>();

  const setOpen = (open: boolean) => {
    if (open === false) setAction(undefined);
  };

  useEffect(() => {
    getPricing();
    return () => resetPricing();
  }, []);

  const actions: ActionsOption[] | undefined = [
    {
      display: <Text tId={'REFRESH_PRICING'} />,
      onClick: () => getPricing(),
      iconName: 'refresh',
    },
    {
      display: <Text tId={'CHANGE_PRICING_NAME'} />,
      onClick: () => setAction(Action.CHANGE_PRICING_NAME),
      iconName: 'edit',
    },
    {
      display: <Text tId={'ADD_PRICING_TAG'} />,
      onClick: () => setAction(Action.ADD_PRICING_TAG),
      iconName: 'label',
    },
    {
      display: <Text tId={'ADD_NEW_PRICING_SPEC'} />,
      onClick: () => setAction(Action.ADD_PRICING_SPEC_FORM),
      iconName: 'add',
    },
    {
      display: <Text tId={'ADD_NEW_PRICING_SPEC_BY_ID'} />,
      onClick: () => setAction(Action.ADD_PRICING_SPEC_BY_ID),
      iconName: 'add',
    },
    {
      display: <Text tId={'REMOVE_ALL_PRICING_SPECS_IN_LENDER_CONTEXT'} />,
      onClick: () => setAction(Action.REMOVE_ALL_PRICING_SPECS_IN_LENDER_CONTEXT),
      iconName: 'delete',
      disabled:
        pricing?.ranges.some((range) => range.lenderEarningsFees && range.lenderEarningsFees.length > 0) === false,
    },
  ];

  return (
    <SubSectionWrapper title={'PRICING_EDITOR'} specificActionsOptionsArray={actions}>
      <>
        {loading ? (
          <Loading />
        ) : !pricing ? (
          <PendingDataWarning boxProps={{ className: 'fade-in-anim', height: 300 }} tId={'PRICING_DOES_NOT_EXIST'} />
        ) : (
          <>
            <Box>
              <Box mt={2} display="flex" justifyContent={'space-between'} alignItems={'center'}>
                <Font variant="h3" color="textPrimary">
                  {pricing.name}
                </Font>
                <Box display={'flex'} gridGap={16}>
                  {pricing.tags.map((tag) => (
                    <div key={`${pricing.id}-${tag}`}>
                      <PricingTag
                        tag={tag}
                        onDelete={async () => {
                          await request(patchRemoveTagFromPricing, { pricingId: pricing.id, tag });
                          await getPricing();
                        }}
                      />
                    </div>
                  ))}
                </Box>
              </Box>
            </Box>
            <Box mb={3}>
              <Font variant={'caption'} color={'textSecondary'}>
                <Text tId="CREATED_AT" /> {formatDateTimeReadable(pricing.createdAt)}
                {' | '}
                <Text tId="UPDATED_AT" /> {formatDateTimeReadable(pricing.updatedAt)}
              </Font>
            </Box>
            <Box mb={3}>
              <Font variant={'body2'} color={'textSecondary'}>
                {pricing.description}
              </Font>
            </Box>
            <PricingSpecsTable editModeAllowed pricing={pricing} refreshData={getPricing} />
            <div style={{ height: '200px' }} />
            <PricingSpecForm open={action === Action.ADD_PRICING_SPEC_FORM} setOpen={setOpen} pricingId={pricing.id} />
            <PricingSpecByIdForm
              open={action === Action.ADD_PRICING_SPEC_BY_ID}
              setOpen={setOpen}
              pricingId={pricing.id}
            />
            <PricingNameForm open={action === Action.CHANGE_PRICING_NAME} setOpen={setOpen} pricing={pricing} />
            <PricingTagForm open={action === Action.ADD_PRICING_TAG} setOpen={setOpen} pricing={pricing} />
            <RemoveAllPricingInLenderContextForm
              open={action === Action.REMOVE_ALL_PRICING_SPECS_IN_LENDER_CONTEXT}
              setOpen={setOpen}
              pricing={pricing}
            />
          </>
        )}
      </>
    </SubSectionWrapper>
  );
};

const Loading = () => (
  <Box
    height={'400px'}
    display={'flex'}
    flexDirection={'column'}
    alignItems={'center'}
    justifyContent={'center'}
    width={'100%'}
  >
    <CircularProgress color={'secondary'} size={48} thickness={3} />
    <Font variant={'subtitle2'} color={'textSecondary'} style={{ paddingTop: 24 }}>
      <Text tId={'LOADING_PRICINGS_DETAILS'} />
    </Font>
  </Box>
);

export const PricingEditorModule = withPermissionsModule(PricingEditor);
